import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useStripe } from '../contexts/StripeContext';
import { sendEmailVerification, auth } from '../firebase';

export default function StripeOnboardingBanner() {
  const { isOnboarded, isLoading: contextLoading, onboardingStatus, startOnboarding, startOnboardingWithoutEmailVerification } = useStripe();
  const [isDismissed, setIsDismissed] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const location = useLocation();

  // Hide banner during onboarding flow
  const isOnboardingPath = location.pathname.startsWith('/app/onboarding');

  // Calculate these values early for visibility logic
  const isEmailVerificationRequired = error?.code === 'auth/unverified-email';
  const isServerStarting = error?.message?.includes('server is currently starting up');

  // Ensure we have the correct field names from the backend response
  const hasStripeAccount = onboardingStatus.has_stripe_account || onboardingStatus.hasStripeAccount;
  const chargesEnabled = onboardingStatus.charges_enabled || onboardingStatus.chargesEnabled;
  const payoutsEnabled = onboardingStatus.payouts_enabled || onboardingStatus.payoutsEnabled;
  
  // Determine if we should show the banner based on detailed status
  const shouldShowBanner = !contextLoading && (
    !hasStripeAccount || // No account at all
    !chargesEnabled || // Can't accept payments
    !payoutsEnabled // Can't receive payouts
  );

  // Don't allow dismissal if they have an incomplete account (to prevent getting stuck)
  const canDismiss = chargesEnabled && payoutsEnabled;

  // Generate compact messaging based on status
  const getBannerMessage = () => {
    if (!hasStripeAccount) {
      return "Get paid by clients instantly";
    } else if (!chargesEnabled || !payoutsEnabled) {
      return "Complete verification to get paid";
    }
    return "Complete payment setup";
  };

  const getBannerTitle = () => {
    if (!hasStripeAccount) {
      return "Payment Setup Required";
    } else if (!chargesEnabled || !payoutsEnabled) {
      return "Action Required";
    }
    return "Setup Incomplete";
  };

  const getUrgencyIcon = () => {
    if (!hasStripeAccount) {
      return (
        <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  // Show skip button after 5 seconds when email verification is required
  useEffect(() => {
    if (isEmailVerificationRequired && !showSkipButton) {
      const timer = setTimeout(() => {
        setShowSkipButton(true);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [isEmailVerificationRequired, showSkipButton]);

  // Hide during onboarding flow
  if (isOnboardingPath) return null;

  // Only show banner if user is not fully onboarded
  if (!shouldShowBanner) return null;
  
  // Allow dismissal for users who are fully onboarded
  if (isDismissed && chargesEnabled && payoutsEnabled) return null;

  const handleOnboardingClick = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await startOnboarding();
    } catch (err) {
      setError(err);
      console.error('Error starting onboarding:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmailVerification = async () => {
    try {
      setIsLoading(true);
      await sendEmailVerification(auth.currentUser);
      setEmailVerificationSent(true);
      console.log('Email verification sent successfully');
    } catch (error) {
      console.error('Error sending email verification:', error);
      setError(new Error('Failed to send verification email. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipEmailVerification = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await startOnboardingWithoutEmailVerification();
    } catch (err) {
      setError(err);
      console.error('Error starting onboarding without email verification:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        className="fixed bottom-4 right-4 bg-white dark:bg-black border border-amber-200 dark:border-amber-500/30 rounded-xl px-4 py-4 shadow-2xl z-50 max-w-sm min-w-[300px]"
        role="alert"
        aria-live="assertive"
        tabIndex={0}
        style={{ 
          boxShadow: '0 8px 32px 0 rgba(245, 158, 11, 0.15), 0 0 0 1px rgba(245, 158, 11, 0.1)' 
        }}
      >
        {/* Header with urgency indicator */}
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 bg-amber-50 dark:bg-amber-500/10 rounded-lg">
            {getUrgencyIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
              {getBannerTitle()}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
              {getBannerMessage()}
            </p>
          </div>
          {canDismiss && (
            <button
              onClick={() => setIsDismissed(true)}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
              aria-label="Dismiss notification"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Progress indicator for incomplete setups */}
        {hasStripeAccount && !onboardingStatus.onboardingComplete && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>Setup Progress</span>
              <span>75%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div className="bg-amber-500 h-1.5 rounded-full w-3/4"></div>
            </div>
          </div>
        )}

        {error && !isEmailVerificationRequired && (
          <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">{error.message}</p>
            {isServerStarting && (
              <button
                onClick={handleOnboardingClick}
                className="text-left text-red-600 dark:text-red-400 hover:text-red-500 dark:hover:text-red-300 font-medium mt-2 text-sm"
              >
                Try again →
              </button>
            )}
          </div>
        )}

        {isEmailVerificationRequired && (
          <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
              {error.message}
            </p>
            {emailVerificationSent ? (
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Email sent! Check your inbox.
              </p>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={handleSendEmailVerification}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Verification'}
                </button>
                {showSkipButton && (
                  <button
                    onClick={handleSkipEmailVerification}
                    className="ml-2 px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-lg"
                    disabled={isLoading}
                  >
                    Skip Verification
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Primary CTA */}
        <button
          onClick={handleOnboardingClick}
          className={`w-full px-4 py-3 bg-brand hover:bg-brand-hover text-black text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl ${
            isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Setting up...
            </>
          ) : (
            <>
              {!hasStripeAccount ? 'Setup Payments Now' : 'Complete Setup'}
            </>
          )}
        </button>

        {/* Value proposition */}
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
          Accept credit cards • Secure processing • Instant deposits
        </div>
      </div>
    </>
  );
}